import React, { useRef, useState } from "react"
import Form, { useForm } from "../Form/Form"
import FormControl from "../Form/FormControl"
import Validators from "../Form/Validators"
import { useOverlay } from "../Overlay/OverlayProvider"
import TextInput, { ITextInput } from "../TextInput/TextInput"
import Button from "../Button/Button"
import { useSnackbar } from "../Snackbar/SnackbarProvider"
import JWTApi from "../../libs/login/JWTApi"
import _ from "lodash"

const SEND_ID = "sendPinDialog"
const ENTER_ID = "enterPinDialog"

function SendPinDlg(props:{api:JWTApi, email:string, onEnter:Function}) {
    const {popOverlay} = useOverlay()
    const email = useRef<ITextInput|null>(null)
    const {valid} = useForm()
    const [busy,setBusy] = useState<boolean>(false)
    const {addSnackbar} = useSnackbar()

    const onContinue = async () => {
        setBusy(true)
        try {
            await props.api.forgot(email.current!.value!)
            popOverlay(SEND_ID)
            props.onEnter()
        }catch(e){
            addSnackbar(e)        
        }finally{
            setBusy(false)
        }
    }
    return <>
        <div className="title">Find Your Account</div>
        <div className="content">
            <p className="grey-text">Enter your e-mail</p>
            <FormControl validators={[Validators.Email()]}>
                <TextInput defaultValue={props.email} ref={email} placeholder="E-mail" float={true}/>
            </FormControl>
        </div>
        <div className="actions">
            <Button onClick={_ => popOverlay(SEND_ID)}>Cancel</Button>
            <Button variant="contained" disabled={busy || !valid} onClick={onContinue}>Continue</Button>
        </div>
    </>
}

function EnterPinDlg(props:{api:JWTApi, email:string, onLogin:Function}) {
    const [busy,setBusy] = useState<boolean>(false)
    const refs = _.range(0, 6).map(i => useRef<ITextInput|null>(null))
    const pins = _.range(0, 6).map(i => useState<string>(""))
    const {valid} = useForm()
    const {popOverlay} = useOverlay()
    const {addSnackbar} = useSnackbar()
    const onKeyDown = (i:number) => (e:React.KeyboardEvent) => {
        if (e.key=="Delete" || e.key=="Backspace") {
            pins[i][1](val => {
                if (!val && i>0) setTimeout(() => refs[i-1].current?.focus())
                return ""
            })
        }
    }
    const onKeyPress = (i:number) => (e:React.KeyboardEvent) => {
        pins[i][1](e.key.toUpperCase())
        if (i<5) setTimeout(() => refs[i+1].current?.focus())
    }
    const onPaste = (index:number) => (e:React.ClipboardEvent) => {
        const text = e.nativeEvent.clipboardData?.getData("text/plain")
        if (!text) return
        const chars = text.slice(0)
        for(var i=index, count = Math.min(6, chars.length); i<count; i++) {
            pins[i][1](chars[i])
        }
        if (i<5) setTimeout(() => refs[i+1].current?.focus())
    }
    const inputs = _.range(0, 6).map(i => <FormControl key={i} validators={[Validators.Required('PIN')]}><TextInput ref={refs[i]} value={pins[i][0]} inputProps={{
        onKeyDown:onKeyDown(i),
        onKeyPress:onKeyPress(i),
        onPaste:onPaste(i)
    }}/></FormControl>)

    const onLogin = async () => {
        setBusy(true)
        try {
            await props.api.login(props.email, pins.map(pin => pin[0]).join(""), true)
            popOverlay(ENTER_ID)
            props.onLogin()
        }catch(e){
            addSnackbar(e)
        }finally{
            setBusy(false)
        }
    }
    return <>
        <div className="title">Forgot password</div>
        <div className="content">
            <p>We sent you an e-mail. Please enter the PIN.</p>
            <div className="pins">{inputs}</div>
        </div>
        <div className="actions">
            <Button onClick={_ => popOverlay(ENTER_ID)}>Cancel</Button>
            <Button variant="contained" disabled={busy || !valid} onClick={onLogin}>Login</Button>
        </div>
    </>
}

export default function usePinDlg(){
    const {addOverlay} = useOverlay()

    const showEnter = (api:JWTApi, email:string, onLogin:Function) => addOverlay(ENTER_ID, {children:<Form><EnterPinDlg api={api} email={email} onLogin={onLogin}/></Form>, className:"dialog login"})
    const sendPin = (api:JWTApi, email:string, onLogin:Function) => addOverlay(SEND_ID, {children:<Form><SendPinDlg api={api} email={email} onEnter={() => showEnter(api, email, onLogin)}/></Form>, className:"dialog login"})
    return {sendPin}
}